import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { handleAuthentication, showSnackBar } from "@containers/App/reducer"
import {
    parseWidgetsData,
    parseFunnelSaleCompleteEventData,
    parseInsuranceValidationFailedDialogData,
    parseInsuranceApplicantDetailsForm,
} from "./parser"

const initialState = {
    isFetching: false,
    isFetched: false,
    error: null,
    widgetsData: {},
    analyticsData: {},
    transactionData: {},
    marketingData: {},
    funnelSaleCompleteEventData: null,
    insuranceApplicantDetailsDialogData: null,
    insuranceValidationFailedDialogData: null,
}

export const orderSuccessSlice = createSlice({
    name: "orderSuccess",
    initialState: initialState,
    reducers: {
        reset: () => initialState,
    },
    extraReducers: (builder) => {
        //handle asynchronous actions
        builder.addCase(fetchInitialData.fulfilled, (state, action) => {
            state.isFetching = false
            state.isFetched = true
            state.error = null
            state.widgetsData = parseWidgetsData(action.payload.widgets)
            state.analyticsData = action.payload.analytics_data
            state.analyticsDataMixpanel = action.payload.analytics_data_mixpanel
            state.globalMixpanelData = action.payload.global_mix_panel_data
            state.transactionData = action.payload?.events?.transaction_data
            state.marketingData = action.payload?.marketing_data
            state.funnelSaleCompleteEventData = parseFunnelSaleCompleteEventData(
                action.payload?.events?.transaction_data
            )
        }),
            builder.addCase(fetchInitialData.pending, (state) => {
                state.isFetching = true
                state.isFetched = false
                state.error = null
            }),
            builder.addCase(fetchInitialData.rejected, (state) => {
                state.isFetching = false
                state.error = true
                state.isFetched = false
            })
        builder.addCase(fetchApplicantDetailsForm.fulfilled, (state, action) => {
            const parsedFormData = parseInsuranceApplicantDetailsForm(
                action.payload?.insurance_applicant_details_bottomsheet?.data
            )
            state.insuranceApplicantDetailsDialogData = parsedFormData
        })
        builder.addCase(submitInsuranceDetails.fulfilled, (state, action) => {
            const dialogData = action.payload?.dialog_data
            if (dialogData) {
                const parsedDialogData = parseInsuranceValidationFailedDialogData(dialogData)
                state.insuranceValidationFailedDialogData = parsedDialogData
            }
        })
    },
})

export const fetchInitialData = createAsyncThunk(
    "orderSuccess/fetchInitialData",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { api } = extra
        const { orderId, aggregator, errorCallback } = _arg
        const cartType = getState().shellReducer.cartType
        try {
            const res = await api.post(`/api/v4/orders/${orderId}/map-payments`, {
                types: cartType,
                payment_aggregator: aggregator,
            })
            if (res.data?.show_insurance_bottomsheet) {
                dispatch(fetchApplicantDetailsForm(orderId))
            }
            return Promise.resolve(res.data)
        } catch (err) {
            if (err.error?.errors) {
                errorCallback?.(err)
            }
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const fetchApplicantDetailsForm = createAsyncThunk(
    "orderSuccess/fetchApplicantDetailsForm",
    async (orderId, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        try {
            const res = await api.get(
                `/api/v1/post_order/insurance/applicant_details_form?group_id=${orderId}`
            )
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const submitInsuranceDetails = createAsyncThunk(
    "orderSuccess/submitInsuranceDetails",
    async (_args, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payload, successCallback } = _args
        try {
            const res = await api.put("/api/v4/subscriptions/update_insurance_details", payload)

            if (res?.is_success) {
                successCallback(res?.data)
            }
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (!onServer && (err?.status_code === 403 || err?.status_code === 401)) {
                dispatch(handleAuthentication())
            } else if (err?.error?.message) {
                showSnackBar(dispatch, err.error.message)
            }
            return rejectWithValue(err)
        }
    }
)

export const orderSuccessReducer = orderSuccessSlice.reducer
export const { reset } = orderSuccessSlice.actions
